import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Upload, X, Image as ImageIcon, Plus, ArrowRight } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const PhotoUpload = ({ onContinue, onSkip }) => {
  const { toast } = useToast();
  const [photos, setPhotos] = useState([]);
  const [uploadingIndex, setUploadingIndex] = useState(null);

  const lifeSections = [
    'Childhood',
    'Teenage Years',
    'Early Adulthood',
    'Family Life',
    'Career',
    'Special Moments',
    'Recent Years'
  ];

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    
    if (photos.length + files.length > 10) {
      toast({
        title: "Too Many Photos",
        description: "You can upload up to 10 photos maximum.",
        variant: "destructive"
      });
      return;
    }

    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "File Too Large",
          description: `${file.name} is larger than 5MB. Please choose a smaller file.`,
          variant: "destructive"
        });
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const newPhoto = {
          id: Date.now() + Math.random(),
          data: event.target.result,
          name: file.name,
          caption: '',
          section: 'Childhood',
          year: ''
        };
        setPhotos(prev => [...prev, newPhoto]);
      };
      reader.readAsDataURL(file);
    });
  };

  const updatePhoto = (id, field, value) => {
    setPhotos(photos.map(photo => 
      photo.id === id ? { ...photo, [field]: value } : photo
    ));
  };

  const removePhoto = (id) => {
    setPhotos(photos.filter(photo => photo.id !== id));
  };

  const handleContinue = () => {
    onContinue(photos);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <ImageIcon className="w-12 h-12 text-purple-500 mx-auto mb-4" />
          <h2 className="text-4xl font-bold text-gray-800 mb-3">
            Add Photos to Your Story
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Bring your autobiography to life with photos from different chapters of your life. 
            Add captions to help tell the story behind each image.
          </p>
          <p className="text-sm text-purple-600 mt-2">
            Optional but recommended • Up to 10 photos • Max 5MB each
          </p>
        </div>

        {/* Upload Area */}
        <div className="bg-white rounded-3xl shadow-xl p-8 mb-8">
          <div className="border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center hover:border-purple-400 transition-colors">
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handlePhotoUpload}
              className="hidden"
              id="photo-upload"
              disabled={photos.length >= 10}
            />
            <label 
              htmlFor="photo-upload" 
              className={`cursor-pointer ${photos.length >= 10 ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-700 font-medium mb-2">
                Click to upload photos or drag and drop
              </p>
              <p className="text-sm text-gray-500">
                PNG, JPG, JPEG up to 5MB • {photos.length}/10 photos uploaded
              </p>
            </label>
          </div>

          {photos.length > 0 && (
            <div className="mt-6 text-right">
              <Button
                onClick={handleContinue}
                className="bg-gradient-to-r from-pink-500 to-purple-500 text-white px-8 py-3 rounded-full border-0"
              >
                Continue with {photos.length} {photos.length === 1 ? 'Photo' : 'Photos'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </div>

        {/* Photos Grid */}
        {photos.length > 0 && (
          <div className="grid md:grid-cols-2 gap-6">
            {photos.map((photo, index) => (
              <div key={photo.id} className="bg-white rounded-2xl shadow-lg overflow-hidden fade-in">
                {/* Image Preview */}
                <div className="relative h-64 bg-gray-100">
                  <img
                    src={photo.data}
                    alt={`Upload ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <button
                    onClick={() => removePhoto(photo.id)}
                    className="absolute top-3 right-3 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 shadow-lg"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <div className="absolute bottom-3 left-3 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                    Photo {index + 1}
                  </div>
                </div>

                {/* Photo Details */}
                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Caption / Description
                    </label>
                    <Textarea
                      placeholder="e.g., My 5th birthday party with family and friends in the backyard"
                      value={photo.caption}
                      onChange={(e) => updatePhoto(photo.id, 'caption', e.target.value)}
                      className="input-focus"
                      rows={3}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Life Section
                      </label>
                      <select
                        value={photo.section}
                        onChange={(e) => updatePhoto(photo.id, 'section', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        {lifeSections.map(section => (
                          <option key={section} value={section}>{section}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Year (Optional)
                      </label>
                      <Input
                        type="text"
                        placeholder="e.g., 1985"
                        value={photo.year}
                        onChange={(e) => updatePhoto(photo.id, 'year', e.target.value)}
                        className="input-focus"
                        maxLength="4"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Action Buttons */}
        <div className="mt-8 flex justify-center gap-4">
          {photos.length === 0 ? (
            <Button
              onClick={() => onSkip()}
              variant="outline"
              className="px-8 py-6 text-lg rounded-full"
            >
              Skip Photos (Continue Without)
            </Button>
          ) : (
            <>
              <Button
                onClick={() => onSkip()}
                variant="outline"
                className="px-6 py-6 rounded-full"
              >
                Skip for Now
              </Button>
              <Button
                onClick={handleContinue}
                className="btn-primary text-white px-8 py-6 text-lg rounded-full border-0"
              >
                Continue with Photos
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </>
          )}
        </div>

        {/* Tips */}
        <div className="mt-8 bg-purple-50 rounded-2xl p-6 max-w-2xl mx-auto">
          <h3 className="font-semibold text-gray-800 mb-3">💡 Tips for Great Photos:</h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li>• <strong>Variety:</strong> Include photos from different life stages</li>
            <li>• <strong>Quality:</strong> Clear, well-lit photos work best</li>
            <li>• <strong>Captions:</strong> Add context - who, what, when, where</li>
            <li>• <strong>Stories:</strong> Photos with good stories make the best additions</li>
            <li>• <strong>Memories:</strong> Include photos that trigger important memories</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PhotoUpload;
